import { useEffect, useState } from 'react'
import { XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Ported as-is from the old AdminPanel.tsx "twitter" tab (Admin Panel
// Redesign didn't call for changes here — just its own section + independent
// data fetching instead of sharing loadData()/activeTab with everything else).
export default function TwitterSection() {
  const { toast } = useToast()
  const [twitterKeywords, setTwitterKeywords] = useState<string[]>([])
  const [twitterLoading, setTwitterLoading] = useState(false)
  const [twitterError, setTwitterError] = useState<string | null>(null)
  const [twitterKeywordsInput, setTwitterKeywordsInput] = useState('')

  useEffect(() => {
    (async () => {
      try {
        setTwitterLoading(true)
        setTwitterError(null)
        const res = await apiClient.getAdminTwitterConfig()
        setTwitterKeywords((res as any).keywords || [])
      } catch (e: any) {
        console.error('Failed to load Twitter config', e)
        setTwitterError(e.message || 'Failed to load Twitter config')
      } finally {
        setTwitterLoading(false)
      }
    })()
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Twitter/X Monitoring</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Configure keywords and search queries for the Twitter live feed on the monitor dashboard. Requires TWITTER_API_KEY.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Add keywords (one per line)</label>
          <textarea
            className="w-full min-h-[100px] text-sm rounded border border-input bg-transparent p-2 font-mono"
            placeholder={'Iran war\n#IranConflict\nfrom:ABORINT\nfrom:IntelCrab'}
            value={twitterKeywordsInput}
            onChange={(e) => setTwitterKeywordsInput(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Supports Twitter search operators: keywords, #hashtags, from:username, "exact phrase". Combined with OR.
          </p>
          <Button
            disabled={twitterLoading}
            onClick={async () => {
              const lines = twitterKeywordsInput
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0)
              if (!lines.length) {
                toast({ title: 'Enter at least one keyword or query', variant: 'destructive' })
                return
              }
              try {
                setTwitterLoading(true)
                setTwitterError(null)
                const merged = [...new Set([...twitterKeywords, ...lines])]
                await apiClient.adminSetTwitterConfig(merged)
                setTwitterKeywords(merged)
                setTwitterKeywordsInput('')
                toast({ title: `Saved ${merged.length} keywords` })
              } catch (e: any) {
                console.error('Failed to save Twitter config', e)
                setTwitterError(e.message || 'Failed to save Twitter config')
              } finally {
                setTwitterLoading(false)
              }
            }}
          >
            {twitterLoading ? 'Saving…' : 'Add Keywords'}
          </Button>
          {twitterError && <p className="text-xs text-destructive mt-1">{twitterError}</p>}
        </div>

        <div className="border rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Active Keywords</h3>
            <span className="text-xs text-muted-foreground">
              {twitterLoading ? 'Loading…' : `${twitterKeywords.length} total`}
            </span>
          </div>
          {twitterKeywords.length === 0 ? (
            <p className="text-sm text-muted-foreground">No keywords configured yet. Add search terms above.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {twitterKeywords.map((kw, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="text-xs font-mono flex items-center gap-1 cursor-pointer hover:bg-destructive/20"
                  onClick={async () => {
                    const updated = twitterKeywords.filter((_, i) => i !== idx)
                    try {
                      setTwitterLoading(true)
                      await apiClient.adminSetTwitterConfig(updated)
                      setTwitterKeywords(updated)
                    } catch (e: any) {
                      setTwitterError(e.message || 'Failed to remove keyword')
                    } finally {
                      setTwitterLoading(false)
                    }
                  }}
                >
                  {kw}
                  <XCircle className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
